/** Chiptune sound effects and a looping attract-mode bassline, all synthesised with WebAudio. */
type Note = [freq: number, dur: number]

class ArcadeAudio {
	private ctx: AudioContext | null = null
	private musicTimer = 0
	private nextNoteAt = 0
	private step = 0
	muted = false

	constructor() {
		try {
			this.muted = localStorage.getItem('wibble:muted') === '1'
		} catch {
			/* storage unavailable */
		}
	}

	/** Must be called from a user gesture before anything will make a sound. */
	unlock(): void {
		if (!this.ctx) {
			try {
				this.ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
			} catch {
				return
			}
		}
		if (this.ctx.state === 'suspended') void this.ctx.resume()
	}

	setMuted(m: boolean): void {
		this.muted = m
		try {
			localStorage.setItem('wibble:muted', m ? '1' : '0')
		} catch {
			/* ignore */
		}
		if (m) this.stopMusic()
	}

	private play(seq: Note[], type: OscillatorType = 'square', gain = 0.05): void {
		if (this.muted || !this.ctx) return
		let t = this.ctx.currentTime
		for (const [f, d] of seq) {
			const o = this.ctx.createOscillator()
			const g = this.ctx.createGain()
			o.type = type
			o.frequency.value = f
			g.gain.setValueAtTime(gain, t)
			g.gain.exponentialRampToValueAtTime(0.0001, t + d)
			o.connect(g).connect(this.ctx.destination)
			o.start(t)
			o.stop(t + d)
			t += d
		}
	}

	coin(): void { this.play([[1047, 0.08], [1319, 0.16]]) }
	start(): void { this.play([[523, 0.06], [659, 0.06], [784, 0.06], [1047, 0.2]]) }
	blip(): void { this.play([[880, 0.04]], 'square', 0.03) }
	error(): void { this.play([[196, 0.15], [131, 0.25]], 'sawtooth', 0.06) }
	fanfare(): void { this.play([[523, 0.1], [659, 0.1], [784, 0.1], [1047, 0.1], [784, 0.1], [1047, 0.35]]) }
	powerUp(): void { this.play([[392, 0.05], [523, 0.05], [659, 0.05], [784, 0.05], [1047, 0.05], [1319, 0.05], [1568, 0.2]], 'triangle', 0.07) }
	tick(): void { this.play([[1500, 0.02]], 'square', 0.02) }
	shoot(): void { this.play([[1200, 0.03], [600, 0.05]], 'square', 0.025) }
	explode(): void { this.play([[220, 0.04], [110, 0.08]], 'sawtooth', 0.05) }
	eat(): void { this.play([[660, 0.04], [990, 0.06]], 'square', 0.035) }

	/** Eight-step bass loop at 132 bpm with a triangle-wave arpeggio on top. */
	startMusic(): void {
		if (this.musicTimer || this.muted || !this.ctx) return
		const ctx = this.ctx
		const bass = [110, 110, 164.8, 110, 130.8, 110, 98, 110]
		const arp = [440, 523.3, 659.3, 880, 659.3, 523.3, 440, 392]
		const stepLen = 60 / 132 / 2
		this.nextNoteAt = ctx.currentTime + 0.05
		const schedule = () => {
			while (this.nextNoteAt < ctx.currentTime + 0.2) {
				const i = this.step % 8
				const t = this.nextNoteAt
				for (const [f, type, gain, len] of [
					[bass[i], 'square', 0.045, stepLen * 0.9],
					[arp[i], 'triangle', 0.03, stepLen * 0.5],
				] as [number, OscillatorType, number, number][]) {
					const o = ctx.createOscillator()
					const g = ctx.createGain()
					o.type = type
					o.frequency.value = f
					g.gain.setValueAtTime(gain, t)
					g.gain.exponentialRampToValueAtTime(0.0001, t + len)
					o.connect(g).connect(ctx.destination)
					o.start(t)
					o.stop(t + len)
				}
				this.nextNoteAt += stepLen
				this.step++
			}
		}
		schedule()
		this.musicTimer = window.setInterval(schedule, 100)
	}

	stopMusic(): void {
		if (this.musicTimer) clearInterval(this.musicTimer)
		this.musicTimer = 0
	}

	get musicPlaying(): boolean {
		return this.musicTimer !== 0
	}
}

export const audio = new ArcadeAudio()
